import * as React from 'react';
import propz from 'propz';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../../consts/report';
import { getStartDateForSchoolYear } from '../../../../../../../helpers/calendar/calendar';
import {
  ReportActions,
  ReportDateRange,
  ReportDescription,
  ReportRadioGroup,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/Reports/ReportFormUi';
import '../../Clubs.css';

const GROUP_BY = {
  SCHOOL_FORMS: 'isGroupByForms',
  YEAR_GROUPS: 'isGroupByYears',
  KEY_STAGES: 'isGroupByKeyStages'
};

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  activeSchoolId: string;
  school: any;
}

const getAcademicYear = (date: Date) => (date.getMonth() >= 8 ? date.getFullYear() : date.getFullYear() - 1);

export const ClubParticipationByClubNumberReportForm = ({ onCloseClick, onSubmit, activeSchoolId, school }: Props) => {
  const initialValues = {
    dateFrom: getStartDateForSchoolYear(new Date()),
    dateTo: new Date(),
    isPrintPP: false,
    isPrintSEN: false,
    isPrintFSM: false,
    isPrintEthnicity: false,
    isPrintEligible: true,
    groupBy: GROUP_BY.SCHOOL_FORMS,
    isGroupByForms: false,
    isGroupByYears: false,
    isGroupByKeyStages: false
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...initialValues,
        isPrintPP: false,
        isPrintSEN: false,
        isPrintFSM: false,
        isPrintEthnicity: false
      }}
      onSubmit={values => {
        const data: any = { ...values };
        propz.set(data, [values.groupBy], true);
        delete data.groupBy;
        onSubmit(data, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.CLUB_PARTICIPATION_BY_CLUB_NUMBER);
      }}
    >
      {({ values, setFieldValue }) => {
        const reportAcademicYear = getAcademicYear(values.dateFrom);
        const isCrossAcademicYear = reportAcademicYear !== getAcademicYear(values.dateTo);
        const isHistoricalPeriod = reportAcademicYear !== getAcademicYear(new Date());
        const formGroupingUnavailable = isHistoricalPeriod;
        const keyStageGroupingUnavailable = propz.get(school, ['ageGroupsNaming'], 'ENGLISH') !== 'ENGLISH';
        const hasInvalidGrouping =
          (values.groupBy === GROUP_BY.SCHOOL_FORMS && formGroupingUnavailable) ||
          (values.groupBy === GROUP_BY.KEY_STAGES && keyStageGroupingUnavailable);
        const status = isCrossAcademicYear
          ? 'Choose dates within one academic year (1 September to 31 August).'
          : hasInvalidGrouping
          ? values.groupBy === GROUP_BY.SCHOOL_FORMS
            ? 'Registration-form history is not stored. Use year groups or key stages for past academic years.'
            : 'Key stages are only available to schools using English year groups.'
          : isHistoricalPeriod &&
            (values.isPrintPP || values.isPrintSEN || values.isPrintFSM || values.isPrintEthnicity)
          ? 'Student breakdowns use the values currently recorded on each student profile.'
          : 'Ready to generate';

        const setDateRangeValue = (field: 'dateFrom' | 'dateTo', date: Date) => {
          setFieldValue(field, date);
          const nextFrom = field === 'dateFrom' ? date : values.dateFrom;
          if (values.groupBy === GROUP_BY.SCHOOL_FORMS && getAcademicYear(nextFrom) !== getAcademicYear(new Date())) {
            setFieldValue('groupBy', GROUP_BY.YEAR_GROUPS);
          }
        };

        return (
          <Form className="eReportForm">
            <div className="eReportForm__scroll">
              <ReportDescription>
                Count each student once by the number of distinct clubs whose session lists included them during the
                selected period.
              </ReportDescription>
              <ReportSection
                number={1}
                title="Reporting period"
                hint="Choose the date range covered by the report."
                required
              >
                <ReportDateRange
                  dateFrom={values.dateFrom}
                  dateTo={values.dateTo}
                  onChangeFrom={date => {
                    setDateRangeValue('dateFrom', date);
                    if (values.dateTo < date) setFieldValue('dateTo', date);
                  }}
                  onChangeTo={date => setDateRangeValue('dateTo', date)}
                />
              </ReportSection>
              <ReportSection number={2} title="Group results" hint="Choose how student participation is organised.">
                <ReportRadioGroup
                  name="groupBy"
                  value={values.groupBy}
                  onChange={value => setFieldValue('groupBy', value)}
                  options={[
                    {
                      value: GROUP_BY.SCHOOL_FORMS,
                      label: 'Registration forms',
                      hint: formGroupingUnavailable
                        ? 'Only available for the current academic year.'
                        : 'Group students by registration form.',
                      disabled: formGroupingUnavailable
                    },
                    { value: GROUP_BY.YEAR_GROUPS, label: 'Year groups', hint: 'Group students by school year.' },
                    {
                      value: GROUP_BY.KEY_STAGES,
                      label: 'Key stages',
                      hint: keyStageGroupingUnavailable
                        ? 'Only available to schools using English year groups.'
                        : 'Group students by key stage.',
                      disabled: keyStageGroupingUnavailable
                    }
                  ]}
                />
              </ReportSection>
              <ReportSection number={3} title="Report content" hint="Choose the totals included in the report.">
                <ReportToggleList>
                  <ReportToggle
                    name="isPrintEligible"
                    label="Show eligible students"
                    hint="Include the number of students eligible to participate."
                    value={values.isPrintEligible}
                    onChange={value => setFieldValue('isPrintEligible', value)}
                  />
                </ReportToggleList>
              </ReportSection>
              <ReportSection number={4} title="Student information" hint="Choose additional information to include.">
                <ReportToggleList>
                  <ReportToggle
                    name="isPrintPP"
                    label="Show PP"
                    hint="Include pupil premium information."
                    value={values.isPrintPP}
                    onChange={value => setFieldValue('isPrintPP', value)}
                  />
                  <ReportToggle
                    name="isPrintSEN"
                    label="Show SEN"
                    hint="Include SEN information."
                    value={values.isPrintSEN}
                    onChange={value => setFieldValue('isPrintSEN', value)}
                  />
                  <ReportToggle
                    name="isPrintFSM"
                    label="Show FSM"
                    hint="Include free school meals information."
                    value={values.isPrintFSM}
                    onChange={value => setFieldValue('isPrintFSM', value)}
                  />
                  <ReportToggle
                    name="isPrintEthnicity"
                    label="Show ethnicity"
                    hint="Include participation totals broken down by ethnicity code."
                    value={values.isPrintEthnicity}
                    onChange={value => setFieldValue('isPrintEthnicity', value)}
                  />
                </ReportToggleList>
              </ReportSection>
            </div>
            <ReportActions
              onCancel={onCloseClick}
              disabled={isCrossAcademicYear || hasInvalidGrouping}
              status={status}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
